import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { AuthContext } from '../App';

/**
 * AIKnowledgeHealthAlert — small "N entries need reindexing" chip pinned
 * to the top of the Admin Portal home so KB drift never goes unnoticed.
 *
 * Only renders when there's actually drift (enabled - embedded > 0).
 * When healthy or empty, it renders nothing — quiet by design.
 *
 * Reads GET /api/admin/ai-knowledge/stats which already carries the
 * embedded_entries count added in iteration 136.
 */
const AIKnowledgeHealthAlert = () => {
  const { API } = useContext(AuthContext);
  const [drift, setDrift] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/ai-knowledge/stats`);
        if (!alive) return;
        const enabled  = data?.enabled_entries  ?? 0;
        const embedded = data?.embedded_entries ?? 0;
        const need = Math.max(0, enabled - embedded);
        if (need > 0) setDrift({ need, enabled });
      } catch {
        // silent — health chip is best-effort
      }
    })();
    return () => { alive = false; };
  }, [API]);

  if (!drift) return null;

  return (
    <div
      className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-center gap-3 flex-wrap"
      data-testid="admin-kb-health-alert"
    >
      <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-amber-900 dark:text-amber-100">
          {drift.need} of {drift.enabled} text entries need reindexing
        </div>
        <div className="text-xs text-amber-800/80 dark:text-amber-200/80">
          These entries won&apos;t appear in citations, auto-cite, Ask an Expert, or lesson AI answers until they&apos;re re-embedded.
        </div>
      </div>
      <Link
        to="/admin/portal#knowledge"
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-3 py-1.5 transition-colors"
        data-testid="admin-kb-health-fix-link"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Reindex now →
      </Link>
    </div>
  );
};

export default AIKnowledgeHealthAlert;
